import { docTypes } from '../../utils/utils'
import {
  SEARCH_REQUEST,
  SEARCH_SUCCESS,
  SEARCH_FAILURE,
  FETCH_RECENT_SEARCHES,
  FETCH_AUTOCOMPLETE_RESULTS,
  RESET_SEARCH_STORE,
  SET_SEARCH_QUERY,
  SET_SEARCH_LOADING_DOCUMENT,
  SET_SEARCH_DOCUMENT,
  SET_SEARCH_IS_MODAL_MOBILE_VISIBLE,
  SEARCH_CLEAR_DOCUMENT,
  SET_SEARCH_DOCUMENT_SOURCE_URL,
  SET_SEARCH_SELECTED_CITATION,
  SET_SELECTED_DOC_TYPES,
  SET_AVAILABLE_DOC_TYPES,
  SET_FILTERED_CITATIONS,
  SET_IS_LOADING_SEARCH_CITATIONS,
  SET_IS_LOADING_AUTO_COMPLETE_SEARCH,
  UPDATE_FROM_AUTO_SELECT,
  RESET_AVAILABLE_DOC_TYPES,
  CANCEL_SEARCH,
  SET_IS_ABROGE_FILTER,
  SET_DATE_FILTER,
  CLEAR_DATE_FILTER,
  SET_DATE_SORT,
  CLEAR_DATE_SORT,
  FILTERED_SEARCH_REQUEST,
  FILTERED_SEARCH_SUCCESS,
  FILTERED_SEARCH_FAILURE,
  PAGINATE_SEARCH_SUCCESS,
  SET_SEARCH_FILTERS,
  CLEAR_SEARCH_FILTERS,
  SET_FILTER_PANEL_OPEN,
  SAVE_SEARCH_SUCCESS,
  DELETE_SAVED_SEARCH_SUCCESS,
  FETCH_SAVED_SEARCHES_SUCCESS,
  TOGGLE_SEARCH_BOOKMARK_SUCCESS,
  FETCH_SEARCH_BOOKMARKS_SUCCESS,
  CHECK_SEARCH_BOOKMARKS_SUCCESS,
  SET_PRE_SEARCH_FILTERS,
  CLEAR_PRE_SEARCH_FILTERS,
  SET_FILTER_MODAL_OPEN,
  FETCH_FILTER_OPTIONS_REQUEST,
  FETCH_FILTER_OPTIONS_SUCCESS,
  FETCH_FILTER_OPTIONS_FAILURE,
} from '../actions/types'

const initialState = {
  searchQuery: '',
  isLoadingSearchCitations: false,
  isLoadingAutoCompleteSearch: false,
  citations: [],
  initialCitations: [],
  error: null,
  recentSearches: [],
  autocompleteResults: [],
  isAbrogeFilter: true,
  dateFilter: null,
  dateSort: null,

  updateFromAutoSelect: null,
  isSearchLoadingDocument: false,
  searchDocument: null,
  isSearchModalVisible: false,
  searchDocumentSourceUrl: null,
  searchSelectedCitation: null,
  availableDocTypes: [],
  selectedDocTypes: [],
  filteredCitations: {},

  // Server-side search state
  searchFilters: {},
  searchFacets: {},
  hadResults: false,   // true once results have been seen; keeps results layout during subsequent searches
  totalResultCount: 0,
  filteredCount: 0,
  currentSearchId: null,
  currentPage: 1,
  pageSize: 15,
  totalPages: 0,
  isFilterPanelOpen: true,

  // Pre-search filters
  preSearchFilters: {},
  filterOptions: {},
  filterOptionsLoading: false,
  filterModalOpen: false,

  // Save & bookmarks
  savedSearches: [],
  bookmarkedDocIds: [],
}

const searchReducer = (state = initialState, action) => {
  switch (action.type) {
    // ─── Legacy client-side actions (kept for backward compatibility) ─────
    case SET_FILTERED_CITATIONS:
      return { ...state, filteredCitations: action.payload };

    case SET_SEARCH_LOADING_DOCUMENT:
      return { ...state, isSearchLoadingDocument: action.payload }

    case SET_SEARCH_DOCUMENT:
      return { ...state, searchDocument: action.payload }

    case SET_SEARCH_SELECTED_CITATION:
      return { ...state, searchSelectedCitation: action.payload }

    case SET_SEARCH_IS_MODAL_MOBILE_VISIBLE:
      return { ...state, isSearchModalVisible: action.payload }

    case SET_SEARCH_DOCUMENT_SOURCE_URL:
      return { ...state, searchDocumentSourceUrl: action.payload }

    case SET_SELECTED_DOC_TYPES:
      return { ...state, selectedDocTypes: action.payload }

    case SET_AVAILABLE_DOC_TYPES:
      return { ...state, availableDocTypes: action.payload }

    case RESET_AVAILABLE_DOC_TYPES:
      return { ...state, availableDocTypes: action.payload }

    case SET_IS_LOADING_SEARCH_CITATIONS:
      return { ...state, isLoadingSearchCitations: action.payload }

    case SET_IS_LOADING_AUTO_COMPLETE_SEARCH:
      return { ...state, isLoadingAutoCompleteSearch: action.payload }

    case UPDATE_FROM_AUTO_SELECT:
      return { ...state, updateFromAutoSelect: action.payload }

    case SEARCH_CLEAR_DOCUMENT:
      return initialState

    case SEARCH_REQUEST:
      return {
        ...state,
        isLoadingSearchCitations: true,
        error: null,
        citations: []
      }

    case CANCEL_SEARCH:
      return {
        ...state,
        isLoadingSearchCitations: false,
        error: null
      }

    case SEARCH_SUCCESS: {
      const initialFilteredCitations = state.isAbrogeFilter
        ? action.payload?.filter(citation => citation.metadata?.is_active !== false)
        : action.payload;
      return {
        ...state,
        isLoadingSearchCitations: false,
        initialCitations: action.payload,
        citations: initialFilteredCitations,
        filteredCitations: initialFilteredCitations,
        error: null
      }
    }

    case SEARCH_FAILURE:
      return {
        ...state,
        isLoadingSearchCitations: false,
        citations: [],
        filteredCitations: [],
        error: action.payload
      }

    case FETCH_RECENT_SEARCHES:
      return {
        ...state,
        autocompleteResults: action.payload
      }

    case FETCH_AUTOCOMPLETE_RESULTS:
      return { ...state, autocompleteResults: action.payload }

    case RESET_SEARCH_STORE:
      return initialState

    case SET_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.payload
      }

    case SET_IS_ABROGE_FILTER: {
      const filteredCitations = action.payload
        ? state.initialCitations?.filter(citation => citation.metadata?.is_active !== false)
        : state.initialCitations;
      return {
        ...state,
        isAbrogeFilter: action.payload,
        filteredCitations: filteredCitations
      }
    }

    case SET_DATE_FILTER:
      return { ...state, dateFilter: action.payload }

    case CLEAR_DATE_FILTER:
      return { ...state, dateFilter: null }

    case SET_DATE_SORT:
      return { ...state, dateSort: action.payload }

    case CLEAR_DATE_SORT:
      return { ...state, dateSort: null }

    // ─── Server-side filtered search actions ─────────────────────────────

    case FILTERED_SEARCH_REQUEST:
      return {
        ...state,
        isLoadingSearchCitations: true,
        error: null,
        citations: [],
        filteredCitations: [],
        currentSearchId: null,
        // Lock results mode the moment a subsequent search starts
        hadResults: state.hadResults || state.totalResultCount > 0,
      }

    case FILTERED_SEARCH_SUCCESS: {
      const { results, facets, total_count, filtered_count, search_id, page, page_size, total_pages } = action.payload
      return {
        ...state,
        isLoadingSearchCitations: false,
        citations: results,
        filteredCitations: results,
        searchFacets: facets,
        totalResultCount: total_count,
        filteredCount: filtered_count,
        currentSearchId: search_id,
        currentPage: page,
        pageSize: page_size,
        totalPages: total_pages,
        error: null,
        hadResults: total_count > 0 ? true : state.hadResults,
      }
    }

    case FILTERED_SEARCH_FAILURE:
      return {
        ...state,
        isLoadingSearchCitations: false,
        citations: [],
        filteredCitations: [],
        error: action.payload,
      }

    case PAGINATE_SEARCH_SUCCESS: {
      const { results, facets, total_count, filtered_count, search_id, page, page_size, total_pages } = action.payload
      return {
        ...state,
        citations: results,
        filteredCitations: results,
        searchFacets: facets,
        totalResultCount: total_count,
        filteredCount: filtered_count,
        currentSearchId: search_id,
        currentPage: page,
        pageSize: page_size,
        totalPages: total_pages,
      }
    }

    case SET_SEARCH_FILTERS:
      return { ...state, searchFilters: action.payload }

    case CLEAR_SEARCH_FILTERS:
      return { ...state, searchFilters: {} }

    case SET_FILTER_PANEL_OPEN:
      return { ...state, isFilterPanelOpen: action.payload }

    // ─── Save Search ─────────────────────────────────────────────────────

    case SAVE_SEARCH_SUCCESS:
      return {
        ...state,
        savedSearches: [action.payload, ...state.savedSearches],
      }

    case FETCH_SAVED_SEARCHES_SUCCESS:
      return { ...state, savedSearches: action.payload }

    case DELETE_SAVED_SEARCH_SUCCESS:
      return {
        ...state,
        savedSearches: state.savedSearches.filter(s => s.id !== action.payload),
      }

    // ─── Search Bookmarks ────────────────────────────────────────────────

    case TOGGLE_SEARCH_BOOKMARK_SUCCESS: {
      const { bookmarked, document_external_id } = action.payload
      const currentIds = [...state.bookmarkedDocIds]
      if (bookmarked) {
        if (!currentIds.includes(document_external_id)) {
          currentIds.push(document_external_id)
        }
      } else {
        const idx = currentIds.indexOf(document_external_id)
        if (idx !== -1) currentIds.splice(idx, 1)
      }
      return { ...state, bookmarkedDocIds: currentIds }
    }

    case FETCH_SEARCH_BOOKMARKS_SUCCESS:
      return {
        ...state,
        bookmarkedDocIds: action.payload.map(b => b.document_external_id),
      }

    case CHECK_SEARCH_BOOKMARKS_SUCCESS:
      return { ...state, bookmarkedDocIds: action.payload }

    // ─── Pre-Search Filters ───────────────────────────────────────────────

    case SET_PRE_SEARCH_FILTERS:
      return { ...state, preSearchFilters: action.payload }

    case CLEAR_PRE_SEARCH_FILTERS:
      return { ...state, preSearchFilters: {} }

    case SET_FILTER_MODAL_OPEN:
      return { ...state, filterModalOpen: action.payload }

    case FETCH_FILTER_OPTIONS_REQUEST:
      return { ...state, filterOptionsLoading: true }

    case FETCH_FILTER_OPTIONS_SUCCESS:
      return { ...state, filterOptions: action.payload, filterOptionsLoading: false }

    case FETCH_FILTER_OPTIONS_FAILURE:
      return { ...state, filterOptionsLoading: false }

    default:
      return state
  }
}

export default searchReducer
